import React from "react";
import { LeagueMatch as LeagueMatchType, FlexMatch } from "../types/league";
import { CheckCircle, Clock } from "lucide-react";

interface LeagueMatchProps {
  match: LeagueMatchType | FlexMatch;
  onClick?: (match: LeagueMatchType | FlexMatch) => void;
  showWeek?: boolean;
  showCourt?: boolean;
  showDate?: boolean;
  variant?: "default" | "compact" | "detailed";
}

const LeagueMatch: React.FC<LeagueMatchProps> = ({
  match,
  onClick,
  showWeek = false,
  showCourt = false,
  showDate = false,
  variant = "default",
}) => {
  const handleClick = () => {
    if (onClick && !match.isCompleted) {
      onClick(match);
    }
  };

  const getStatusIcon = () => {
    if (match.isCompleted) {
      return <CheckCircle className="w-4 h-4 text-green-600" />;
    }
    return <Clock className="w-4 h-4 text-blue-600" />;
  };

  const getStatusText = () => {
    if (match.isCompleted) {
      return "Completed";
    }
    return "Pending";
  };

  const getMatchCardClass = () => {
    const baseClass = "border rounded-lg p-4 transition-shadow";
    const clickableClass =
      onClick && !match.isCompleted ? "cursor-pointer hover:shadow-md" : "";
    const statusClass = match.isCompleted
      ? "bg-green-50 border-green-200"
      : "bg-blue-50 border-blue-200";

    return `${baseClass} ${statusClass} ${clickableClass}`;
  };

  if (variant === "compact") {
    return (
      <div className={getMatchCardClass()} onClick={handleClick}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {getStatusIcon()}
            <div className="text-sm">
              <div className="font-medium text-gray-900">
                {match.team1.name} vs {match.team2.name}
              </div>
              {showWeek && "week" in match && (
                <div className="text-xs text-gray-500">Week {match.week}</div>
              )}
            </div>
          </div>
          {match.isCompleted && match.result && (
            <div className="text-sm font-medium text-gray-900">
              {match.result.team1Score} - {match.result.team2Score}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (variant === "detailed") {
    return (
      <div className={getMatchCardClass()} onClick={handleClick}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            {getStatusIcon()}
            <span className="text-sm font-medium text-gray-700">
              {getStatusText()}
            </span>
          </div>
          <div className="flex items-center space-x-4 text-xs text-gray-500">
            {showWeek && "week" in match && <span>Week {match.week}</span>}
            {showCourt && match.court && <span>Court {match.court}</span>}
            {showDate && match.scheduledDate && (
              <span>{new Date(match.scheduledDate).toLocaleDateString()}</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 items-center">
          {/* Team 1 */}
          <div className="text-center">
            <div className="font-semibold text-gray-900 mb-1">
              {match.team1.name}
            </div>
            <div className="text-sm text-gray-600">
              {match.team1.player1} & {match.team1.player2}
            </div>
            {match.isCompleted && match.result && (
              <div className="text-lg font-bold text-gray-900 mt-2">
                {match.result.team1Score}
              </div>
            )}
          </div>

          {/* VS */}
          <div className="text-center">
            <div className="text-gray-500 font-medium text-sm">VS</div>
            {match.isCompleted && match.result && (
              <div className="mt-2">
                <div className="text-xs text-gray-500">Winner</div>
                <div className="text-sm font-medium text-green-600">
                  {match.result.winner === match.team1.id
                    ? match.team1.name
                    : match.team2.name}
                </div>
              </div>
            )}
          </div>

          {/* Team 2 */}
          <div className="text-center">
            <div className="font-semibold text-gray-900 mb-1">
              {match.team2.name}
            </div>
            <div className="text-sm text-gray-600">
              {match.team2.player1} & {match.team2.player2}
            </div>
            {match.isCompleted && match.result && (
              <div className="text-lg font-bold text-gray-900 mt-2">
                {match.result.team2Score}
              </div>
            )}
          </div>
        </div>

        {match.notes && (
          <div className="mt-3 p-2 bg-gray-100 rounded text-xs text-gray-600">
            {match.notes}
          </div>
        )}

        {!match.isCompleted && onClick && (
          <div className="mt-3 text-center">
            <span className="text-sm text-blue-600 font-medium">
              Click to Enter Score
            </span>
          </div>
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div className={getMatchCardClass()} onClick={handleClick}>
      <div className="text-center mb-3">
        <div className="font-semibold text-gray-900">{match.team1.name}</div>
        <div className="text-sm text-gray-600">
          {match.team1.player1} & {match.team1.player2}
        </div>
      </div>

      <div className="text-center text-gray-500 font-medium mb-3">VS</div>

      <div className="text-center mb-3">
        <div className="font-semibold text-gray-900">{match.team2.name}</div>
        <div className="text-sm text-gray-600">
          {match.team2.player1} & {match.team2.player2}
        </div>
      </div>

      {match.isCompleted && match.result ? (
        <div className="text-center">
          <div className="text-lg font-bold text-gray-900">
            {match.result.team1Score} - {match.result.team2Score}
          </div>
          <div className="text-sm text-gray-600">
            Winner:{" "}
            {match.result.winner === match.team1.id
              ? match.team1.name
              : match.team2.name}
          </div>
        </div>
      ) : (
        <div className="text-center">
          <span className="text-sm text-blue-600 font-medium">
            Click to Enter Score
          </span>
        </div>
      )}

      {(showWeek || showCourt || showDate) && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex items-center justify-center space-x-4 text-xs text-gray-500">
            {showWeek && "week" in match && <span>Week {match.week}</span>}
            {showCourt && match.court && <span>Court {match.court}</span>}
            {showDate && match.scheduledDate && (
              <span>{new Date(match.scheduledDate).toLocaleDateString()}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeagueMatch;
